/**
 * DashboardBlade — shared right-anchored drawer shell for the site-dashboard
 * blades (Alarms / Maintenance / Commands / Camera schedules).
 *
 * Owns the common chrome every blade repeated by hand: the Drawer + paper
 * styling, the title/close header (with an optional subtitle and an optional
 * header action), and an optional bordered footer. Each blade supplies its own
 * body — tabs, segmented toggles, lists, grids — as children.
 *
 * Contains:
 * - DashboardBlade — the drawer shell (default export)
 */
import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

const PAPER_SX = {
  bgcolor: 'background.paper',
  borderLeft: 1,
  borderColor: 'var(--md-outline-variant)',
  display: 'flex',
  flexDirection: 'column',
};
const HEADER_SX = {
  px: 'var(--page-gap)',
  py: 2,
  borderBottom: 1,
  borderColor: 'var(--md-outline-variant)',
  flexShrink: 0,
};
const TITLE_SX = { fontSize: 'var(--title-size)', fontWeight: 'var(--title-weight)', lineHeight: 1.2 };
const FOOTER_SX = {
  px: 'var(--page-gap)',
  py: 1.5,
  borderTop: 1,
  borderColor: 'var(--md-outline-variant)',
  flexShrink: 0,
};

export default function DashboardBlade({ open, onClose, width, title, subtitle, headerAction, footer, children }) {
  const { t } = useTranslation();

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width, ...PAPER_SX } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={HEADER_SX}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={TITLE_SX}>{title}</Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {headerAction}
        <IconButton onClick={onClose} aria-label={t('actions.close', 'Close')} size="small" color="primary">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {children}

      {footer ? (
        <Stack direction="row" alignItems="center" sx={FOOTER_SX}>
          {footer}
        </Stack>
      ) : null}
    </Drawer>
  );
}

DashboardBlade.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  /** Drawer paper width (MUI responsive width value). */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  title: PropTypes.node,
  subtitle: PropTypes.node,
  /** Optional action element rendered between the title and the close button. */
  headerAction: PropTypes.node,
  /** Optional footer content, rendered in a bordered footer row. */
  footer: PropTypes.node,
  children: PropTypes.node,
};
